import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  makeStyles,
  Typography,
  Box,
  Divider,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import WarningIcon from '@material-ui/icons/Warning';
import { CreateGcpProjectInput, HomepageGcpProject } from '../../types';

const useStyles = makeStyles(theme => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  groupSection: {
    marginTop: theme.spacing(2),
  },
  groupLabel: {
    marginBottom: theme.spacing(1),
  },
  error: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
  helpText: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
  },
  deleteSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.error.light + '20',
    borderRadius: theme.shape.borderRadius,
  },
  deleteWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.error.main,
    marginBottom: theme.spacing(1),
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
  confirmDialog: {
    '& .MuiDialogContent-root': {
      paddingTop: theme.spacing(2),
    },
  },
  confirmWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
}));

interface GcpProjectContributionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateGcpProjectInput) => Promise<void>;
  userGroups: string[];
  userRef?: string;
  editingProject?: HomepageGcpProject | null;
  onDelete?: (projectId: string) => Promise<void>;
}

/**
 * Validates a GCP project ID format.
 * GCP project IDs must be 6-30 lowercase letters, digits, or hyphens.
 * Must start with a letter. Cannot end with a hyphen.
 */
function isValidGcpProjectId(projectId: string): boolean {
  const pattern = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/;
  return pattern.test(projectId);
}

export const GcpProjectContributionDialog = ({
  open,
  onClose,
  onSubmit,
  userGroups,
  userRef,
  editingProject,
  onDelete,
}: GcpProjectContributionDialogProps) => {
  const classes = useStyles();
  const [projectId, setProjectId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isEditMode = Boolean(editingProject);

  // Set initial values when dialog opens
  useEffect(() => {
    if (open) {
      if (editingProject) {
        setProjectId(editingProject.projectId);
        setDisplayName(editingProject.displayName || '');
        setSelectedGroups(editingProject.groups);
      } else {
        // Reset form for add mode
        setProjectId('');
        setDisplayName('');
        setSelectedGroups([]);
      }
      setError(null);
    }
  }, [open, editingProject]);

  const resetForm = useCallback(() => {
    setProjectId('');
    setDisplayName('');
    setSelectedGroups([]);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleGroupToggle = useCallback((groupRef: string) => {
    setSelectedGroups(prev =>
      prev.includes(groupRef)
        ? prev.filter(g => g !== groupRef)
        : [...prev, groupRef],
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    // Validation
    if (!projectId.trim()) {
      setError('Project ID is required');
      return;
    }
    if (!isValidGcpProjectId(projectId.trim())) {
      setError('Invalid GCP project ID format. Must be 6-30 characters, lowercase letters, digits, or hyphens. Must start with a letter.');
      return;
    }
    if (selectedGroups.length === 0) {
      setError('Select at least one option (Myself or a group)');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      await onSubmit({
        projectId: projectId.trim(),
        displayName: displayName.trim() || undefined,
        groupRefs: selectedGroups,
      });
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save project');
    } finally {
      setSubmitting(false);
    }
  }, [projectId, displayName, selectedGroups, onSubmit, handleClose]);

  const handleDelete = useCallback(async () => {
    if (!editingProject || !onDelete) return;

    setDeleting(true);
    try {
      await onDelete(editingProject.id);
      setConfirmDeleteOpen(false);
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete project');
    } finally {
      setDeleting(false);
    }
  }, [editingProject, onDelete, handleClose]);

  const formatGroupName = (groupRef: string) => {
    const parts = groupRef.split('/');
    const name = parts[parts.length - 1];
    return name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' ');
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditMode ? 'Edit' : 'Add'} GCP Project
        </DialogTitle>
        <DialogContent>
          <TextField
            className={classes.field}
            label="Project ID"
            value={projectId}
            onChange={e => setProjectId(e.target.value.toLowerCase())}
            variant="outlined"
            fullWidth
            required
            disabled={isEditMode}
            inputProps={{ maxLength: 30 }}
            helperText={isEditMode ? 'Project ID cannot be changed' : 'The GCP project ID (e.g., my-project-123)'}
          />

          <TextField
            className={classes.field}
            label="Display Name (optional)"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            variant="outlined"
            fullWidth
            inputProps={{ maxLength: 200 }}
            helperText="A friendly name for the project"
          />

          <FormControl component="fieldset" className={classes.groupSection}>
            <FormLabel component="legend" className={classes.groupLabel}>
              Add for *
            </FormLabel>
            <FormGroup>
              {userRef && (
                <FormControlLabel
                  key="myself"
                  control={
                    <Checkbox
                      checked={selectedGroups.includes(userRef)}
                      onChange={() => handleGroupToggle(userRef)}
                      color="primary"
                    />
                  }
                  label="Myself (personal project)"
                />
              )}
              {userGroups.map(groupRef => (
                <FormControlLabel
                  key={groupRef}
                  control={
                    <Checkbox
                      checked={selectedGroups.includes(groupRef)}
                      onChange={() => handleGroupToggle(groupRef)}
                      color="primary"
                    />
                  }
                  label={formatGroupName(groupRef)}
                />
              ))}
            </FormGroup>
            {!userRef && userGroups.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                You are not a member of any groups.
              </Typography>
            )}
          </FormControl>

          {isEditMode && onDelete && (
            <>
              <Divider style={{ marginTop: 24 }} />
              <Box className={classes.deleteSection}>
                <div className={classes.deleteWarning}>
                  <WarningIcon fontSize="small" />
                  <Typography variant="body2">
                    Permanently delete this project
                  </Typography>
                </div>
                <Button
                  variant="contained"
                  className={classes.deleteButton}
                  startIcon={<DeleteIcon />}
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={deleting}
                >
                  Delete Project
                </Button>
              </Box>
            </>
          )}

          {error && (
            <Typography className={classes.error} variant="body2">
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting || deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={submitting || deleting || (!userRef && userGroups.length === 0)}
          >
            {submitting ? (isEditMode ? 'Saving...' : 'Adding...') : (isEditMode ? 'Save' : 'Add Project')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        className={classes.confirmDialog}
      >
        <DialogTitle>Delete GCP Project?</DialogTitle>
        <DialogContent>
          <div className={classes.confirmWarning}>
            <WarningIcon />
            <Typography>This action cannot be undone.</Typography>
          </div>
          <Typography variant="body2" color="textSecondary">
            Are you sure you want to permanently delete "{editingProject?.displayName || editingProject?.projectId}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            className={classes.deleteButton}
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
